import React, { useEffect, useRef, useState } from "react";
import { Box, TextField, IconButton, styled } from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { useIntlId } from "../../../../../hooks/use-intl-id";
import { useMutation } from "@apollo/client";
import { POST_MESSAGE } from "../../../../../apollo-client/mutations";

import { useParams } from "react-router-dom";
import { showErrorToast } from "../../../../../toast/toast-service";
import { isEmpty } from "lodash";
import { useChatStore } from "../../../../../store/chatStore";

export const ChatInputBar = ({
  chosenChat,
  setPostingMessage,
}) => {
  const [inputMessage, setInputMessage] = useState("");
  const { id } = useParams();
  const inputRef = useRef(null);

  // Use centralized chat store
  const { updateChosenChat, resetTeachingRequest, isChatOpen } = useChatStore();

  const enterMessagePlaceholder = useIntlId("chat.enter-message-placeholder");

  const [postMessage, {}] = useMutation(POST_MESSAGE, {
    onCompleted: (data) => {
      setPostingMessage("");

      const chatObject = data.postMessage;

      if (chatObject.messagesCount === 1) {
        // Update the chosen chat in store with new chat data
        const newChatData = {
          type: chatObject.type,
          chatTitle: chatObject.title,
          profilePicture:
            chatObject.picture || chatObject.otherPrivateChatMember.profilePicture,
          receiverId: chatObject.otherPrivateChatMember._id,
          chatId: chatObject._id,
          unreadMessagesCount: chatObject.unreadMessagesCount,
          isOnVacation: chatObject.otherPrivateChatMember.onHoliday,
        };
        updateChosenChat(newChatData);
      }
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
    ignoreResults: true,
  });

  const sendMessage = async () => {
    // Check if the message has actual content (not just whitespace or newlines)
    const trimmedMessage = inputMessage.trim();
    if (trimmedMessage.length > 0) {
      setPostingMessage(trimmedMessage); // Trigger optimistic response with trimmed message
      setInputMessage(""); // Clear input immediately
      await postMessage({
        variables: {
          type: chosenChat.type,
          message: trimmedMessage, // Send trimmed message to server
          receiver: chosenChat.receiverId,
          isTeachingRequest: chosenChat.isTeachingRequest || false,
          course: id,
        },
      });
      resetTeachingRequest();
    }
  };

  const onKeyDown = async (event) => {
    if (event.keyCode === 13 && !event.shiftKey) {
      event.preventDefault(); // Prevent default behavior (new line)
      // Only attempt to send if there's actual content after trimming
      await sendMessage();
    }
  };

  const handleSendButtonClick = async () => {
    await sendMessage();
  };

  const focusInput = () => {
    if (isChatOpen) inputRef.current?.focus();
  };

  // focus input when selecting a new chat
  useEffect(() => {
    focusInput();
  }, [chosenChat]);

  return (
    <Container>
      <Input
        ref={inputRef}
        disabled={!isEmpty(chosenChat?.isBlockedBy)}
        placeholder={enterMessagePlaceholder}
        value={inputMessage}
        onChange={(event) => setInputMessage(event.target.value)}
        onKeyDown={onKeyDown}
        multiline
        maxRows={4}
        variant="outlined"
        sx={{
          flexGrow: 1,
          marginLeft: "8px",
          "& .MuiOutlinedInput-root": {
            border: "none",
            outline: "none",
            fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: "15px",
            fontWeight: "400",
            wordBreak: "break-word",
            backgroundColor: "transparent",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "& fieldset": {
              border: "none",
            },
            "&:hover fieldset": {
              border: "none",
            },
            "&.Mui-focused fieldset": {
              border: "none",
            },
            "&.Mui-focused": {
              transform: "scale(1.02)",
            },
          },
          "& .MuiInputBase-input": {
            padding: "12px 16px",
            maxHeight: "120px",
            overflowY: "auto",
            color: "#2C3E50",
            "&::placeholder": {
              color: "#95A5A6",
              opacity: 0.8,
              fontWeight: "400",
            },
            "&::-webkit-scrollbar-track": {
              borderRadius: "10px",
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar": {
              width: "6px",
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: "10px",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
            "&:hover::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          },
        }}
      />
      <SendButton
        onClick={handleSendButtonClick}
        disabled={!isEmpty(chosenChat?.isBlockedBy)}
        sx={{
          margin: "0 8px",
          width: "44px",
          height: "44px",
          backgroundColor: (theme) => theme?.mainColor || "#E87975",
          color: "white",
          boxShadow: "0 4px 12px rgba(232, 121, 117, 0.3)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: (theme) => theme?.hoverColor || "#f86b6b",
            transform: "translateY(-2px) scale(1.05)",
            boxShadow: "0 8px 20px rgba(232, 121, 117, 0.4)",
          },
          "&:active": {
            transform: "translateY(0) scale(0.98)",
          },
          "&:disabled": {
            backgroundColor: "#E0E0E0",
            color: "#9E9E9E",
            boxShadow: "none",
            transform: "none",
          },
        }}
      >
        <SendIcon sx={{ fontSize: "20px" }} />
      </SendButton>
    </Container>
  );
};

const Container = styled(Box)(({ theme }) => ({
  width: "calc(100% - 20px)",
  marginBottom: "10px",
  marginLeft: "10px",
  marginRight: "10px",
  marginTop: "8px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 12px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  borderRadius: "24px",
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(20px)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)",
    transform: "translateY(-1px)",
  },
  "&:focus-within": {
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08)",
    border: `1px solid ${theme?.mainColor || "#E87975"}20`,
  },
}));

const Input = styled(TextField)({});

const SendButton = styled(IconButton)({});
